'use client'

import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { IChildrenProps } from '@/utils/typings/children-props'
import {
    AUTH_STEP_PATH_PARAMS,
    AUTH_STEPS,
    IAuthStep,
    IAuthStepQueryParams,
} from '@/features/auth/helpers/auth-constants'
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useGetBrandQuery } from '@/api/brands/queries/get-brand-query'

interface IAuthContext {
    currentStep: IAuthStep
    setCurrentStep: (step: IAuthStep, queryParams?: Partial<IAuthStepQueryParams>) => void
    /** Query params stored in React state (used in state-driven modal flows where URL can't be updated). */
    stateParams: Partial<IAuthStepQueryParams>
    brandSlug: string | undefined
    boardReferralCode: string | null | undefined
    withSwitchToSignup?: boolean
}

const AuthPageContext = createContext<IAuthContext | null>(null)

type IAuthProviderProps = IChildrenProps & {
    withSwitchToSignup?: boolean
    initialStep?: IAuthStep
    boardReferralCode?: string | null
}

export function AuthProvider({
    children,
    withSwitchToSignup = true,
    initialStep,
    boardReferralCode,
}: IAuthProviderProps) {
    const params = useParams()
    const router = useRouter()
    const pathname = usePathname()
    const searchParams = useSearchParams()

    const brandSlug = searchParams.get('brand_slug') || undefined

    const { isLoading: isBrandLoading } = useGetBrandQuery(false, brandSlug)

    const [rawCurrentStep, setRawCurrentStep] = useState<IAuthStep | null>(null)
    const [stateParams, setStateParams] = useState<Partial<IAuthStepQueryParams>>({})

    const urlStep = useMemo(() => {
        return (AUTH_STEP_PATH_PARAMS.AuthStep in params ? params.auth_step : undefined) as IAuthStep | undefined
    }, [params])

    const currentStep = useMemo(() => {
        if (rawCurrentStep) return rawCurrentStep

        if (urlStep) return urlStep

        return initialStep ?? AUTH_STEPS.SignUp
    }, [rawCurrentStep, urlStep, initialStep])

    const setCurrentStep = useCallback(
        (step: IAuthStep, queryParams?: Partial<IAuthStepQueryParams>) => {
            setRawCurrentStep(step)

            if (queryParams) {
                setStateParams((prev) => ({ ...prev, ...queryParams }))
            }

            const currentQueryParams = new URLSearchParams(searchParams)

            if (queryParams) {
                const newQueryParams = new URLSearchParams(queryParams)
                newQueryParams.forEach((value, key) => {
                    currentQueryParams.set(key, value)
                })
            }

            const path = pathname.split('/')
            const authStepPath = path.findIndex((segment) => segment === params.auth_step)

            if (authStepPath === -1) return

            path[authStepPath] = step

            const queryString = currentQueryParams.toString()

            router.push(`${path.join('/')}${queryString ? `?${queryString}` : ''}`)
        },
        [params.auth_step, pathname, router, searchParams]
    )

    // We do that because we need the query to finish loading before rendering the form.
    // Otherwise the form default values will not be applied to react-hook-form..
    if (isBrandLoading) return null

    return (
        <AuthPageContext.Provider
            value={{
                currentStep,
                setCurrentStep,
                stateParams,
                brandSlug: brandSlug || undefined,
                boardReferralCode,
                withSwitchToSignup,
            }}
        >
            {children}
        </AuthPageContext.Provider>
    )
}

export function useAuthPageContext() {
    const context = useContext(AuthPageContext)

    if (!context) throw new Error('AuthPageContext not found')

    return context
}
